// Merkan · Grundlayout (GR-05, GR-13, GR-14)
// Menü links mit Abschnitten, sichtbar nur mit Recht; Kopfleiste mit Suchfeld (auch Strg /),
// Assistent (Strg K) und Präsentationsmodus; untere Leiste auf Mobilgeräten.
import { useErscheinen } from '../ui/erscheinen';
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowLeftRight, Repeat, Mail, Eye, EyeOff, Languages, LifeBuoy, LogOut, Monitor, Moon, PanelLeftClose, PanelLeftOpen, ScanBarcode, Sun } from 'lucide-react';
import { IcSuche as Search, IcMehr } from '../ui/icons';
import { IcArtikel, IcSendungen, IcKiAssistent, IcAuswertungen, IcEinstellungen, IcPosteingang, IcKunden, IcLieferanten, IcRechnungen, IcSeefracht, IcDashboard, IcAuftraege, IcWiki, IcDebitoren, IcKreditoren, IcZahlungseingang } from '../ui/icons';
import { useSession } from '../lib/session';
import { useVorliebe } from '../lib/entwurf';
import { aktuelleBranche } from '../lib/branche';
import { supabase } from '../lib/supabase';
import { initialen } from '../lib/format';
import { t, sprache, spracheSetzen } from '../lib/sprache';
import { Knopf } from '../ui';
import { Logo } from '../ui/logo';
import { DokumentLesen } from '../ui/dokument-lesen';
import { DokumentAnsicht } from '../ui/dokument-ansicht';
import { handscannerLauschen } from '../lib/scan';
import Starter, { type StarterArt } from '../seiten/assistent/Starter';
import type { AssistentModus } from '../seiten/assistent/Assistent';
import Tour from '../hilfe/Tour';
import HilfeSymbol from '../hilfe/HilfeSymbol';
import { supportOeffnen } from '../seiten/benachrichtigungen/SupportDialog';
import Glocke from '../seiten/benachrichtigungen/Glocke';
import KiHinweis from '../seiten/benachrichtigungen/KiHinweis';

import { AnsichtAlsLeiste } from '../ui/ansicht-als';
const Assistent = lazy(() => import('../seiten/assistent/Assistent'));
const Suche = lazy(() => import('../seiten/assistent/Suche'));
const ScanFenster = lazy(() => import('../ui/scan').then((m) => ({ default: m.ScanFenster })));

export interface MenuePunkt { zu: string; name: string; icon: ReactNode; rechte: string[] | null; zaehler?: number | null; ende?: boolean; /** eingerückt unter dem Punkt davor */ unter?: boolean }

export function menue(): { abschnitt: string | null; punkte: MenuePunkt[] }[] {
  // Gruppen nach Kategorie und Funktion: Arbeitsbereich, Verkauf, Finanzen, Stammdaten, Wissen. Einstellungen stehen im Fuß.
  return [
    { abschnitt: t('Arbeitsbereich'), punkte: [
      { zu: '/', name: t('Übersicht'), icon: <IcDashboard />, rechte: null, ende: true },
      { zu: '/hub', name: t('Service Hub'), icon: <IcPosteingang />, rechte: ['hub'] },
      { zu: '/postfach', name: t('Postfach'), icon: <Mail />, rechte: ['hub'], unter: true },
    ] },
    { abschnitt: t('Verkauf'), punkte: [
      { zu: '/vorgaenge', name: t('Vorgänge'), icon: <IcAuftraege />, rechte: ['verkauf'] },
      { zu: '/reklamationen', name: t('Reklamationen'), icon: <AlertTriangle />, rechte: ['verkauf', 'einkauf', 'versand', 'rw'], unter: true },
      { zu: '/bedarfe', name: t('Wiederkehrende Bedarfe'), icon: <Repeat />, rechte: ['verkauf'], unter: true },
    ] },
    { abschnitt: t('Finanzen'), punkte: [
      { zu: '/rechnungswesen', name: t('Debitoren'), icon: <IcDebitoren />, rechte: ['rw'] },
      { zu: '/kreditoren', name: t('Kreditoren'), icon: <IcKreditoren />, rechte: ['rw'] },
      { zu: '/bank', name: t('Bank und Abschluss'), icon: <IcZahlungseingang />, rechte: ['zahlung.buchen', 'datev.export', 'ustva.sehen', 'gobd.export'] },
      { zu: '/auswertungen', name: t('Auswertungen'), icon: <IcAuswertungen />, rechte: ['auswertung'] },
    ] },
    { abschnitt: t('Stammdaten'), punkte: [
      { zu: '/kunden', name: t('Kunden'), icon: <IcKunden />, rechte: ['stamm', 'verkauf', 'hub', 'rw', 'einkauf'] },
      { zu: '/lieferanten', name: t('Lieferanten'), icon: <IcLieferanten />, rechte: ['stamm', 'einkauf'] },
      ...(aktuelleBranche().schiffe ? [{ zu: '/schiffe', name: t('Schiffe'), icon: <IcSeefracht />, rechte: ['stamm', 'verkauf', 'hub', 'versand'] }] : []),
      { zu: '/artikel', name: t('Artikel'), icon: <IcArtikel />, rechte: ['stamm', 'verkauf', 'einkauf', 'versand'] },
      { zu: '/versanddienstleister', name: t('Versanddienstleister'), icon: <IcSendungen />, rechte: ['versand'] },
      { zu: '/abgleich', name: t('Abgleich'), icon: <ArrowLeftRight />, rechte: ['stamm.bearbeiten'] },
    ] },
    { abschnitt: t('Wissen'), punkte: [
      // Für alle sichtbar: ohne Recht wiki führt der Punkt zu den Anleitungen
      { zu: '/wiki', name: t('Wiki'), icon: <IcWiki />, rechte: null },
    ] },
  ];
}

type Darstellung = 'system' | 'hell' | 'dunkel';
const DARSTELLUNG_FOLGE: Record<Darstellung, Darstellung> = { hell: 'dunkel', dunkel: 'system', system: 'hell' };
const darstellungName = (d: Darstellung) => (d === 'hell' ? t('Hell') : d === 'dunkel' ? t('Dunkel') : t('System'));

/** Kurze Farbüberblendung beim Wechsel der Darstellung (160 ms), entfällt bei reduzierter Bewegung */
function farbenUeberblenden() {
  if (typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const id = 'merkan-darstellung-wechsel';
  document.getElementById(id)?.remove();
  const st = document.createElement('style');
  st.id = id;
  st.textContent = '*, *::before, *::after { transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease, fill 160ms ease, box-shadow 160ms ease !important; }';
  document.head.appendChild(st);
  window.setTimeout(() => st.remove(), 220);
}

function DarstellungKnopf() {
  const { einstellungen, einstellungSetzen } = useSession();
  const jetzt = (einstellungen.darstellung ?? 'system') as Darstellung;
  const naechste = DARSTELLUNG_FOLGE[jetzt];
  const titel = t('Darstellung: {jetzt}. Wechseln zu {naechste}', { jetzt: darstellungName(jetzt), naechste: darstellungName(naechste) });
  return (
    <Knopf
      art="leise"
      icon={jetzt === 'hell' ? <Sun /> : jetzt === 'dunkel' ? <Moon /> : <Monitor />}
      onClick={() => { farbenUeberblenden(); void einstellungSetzen({ darstellung: naechste }); }}
      title={titel}
      aria-label={titel}
      data-darstellung-knopf={jetzt}
      data-tour="kopf-darstellung"
    />
  );
}

function MandantKasten() {
  const { mandanten, mandant, mandantWaehlen } = useSession();
  if (!mandant) return null;
  const gruppen = mandant.gruppen
    .map((g) => (g.split('_Merkan_')[1] ?? g).replace('Geschaeftsfuehrung', 'Geschäftsführung').replace(/_/g, ' '))
    .map((g) => t(g)).join(', ') || t('Keine Gruppe');
  return (
    <div className="navi-mandant" data-tour="navi-mandant">
      {mandanten.length > 1 ? (
        <select value={mandant.mandant_id} onChange={(e) => mandantWaehlen(e.target.value)} aria-label={t('Mandant wählen')}>
          {mandanten.map((m) => <option key={m.mandant_id} value={m.mandant_id}>{m.name}</option>)}
        </select>
      ) : (
        <span className="name">{mandant.name}</span>
      )}
      <span className="rolle">{gruppen}</span>
    </div>
  );
}

export default function Rahmen({ children }: { children: ReactNode }) {
  const { session, hat, abmelden, mandant, einstellungen, einstellungSetzen } = useSession();
  const [suche, setSuche] = useState(false);
  const [sucheAnfang, setSucheAnfang] = useState('');
  // Schmale Bildschirme: kurzer Platzhalter im Suchfeld, damit er nicht abgeschnitten wird
  const [schmal, setSchmal] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 560px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 560px)');
    const neu = () => setSchmal(mq.matches);
    mq.addEventListener('change', neu);
    return () => mq.removeEventListener('change', neu);
  }, []);
  // Assistent: offen als Fenster oder groß, sonst als Leiste oder (nach X) als Symbol unten rechts
  const lies = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
  const schreib = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* egal */ } };
  const [assistent, setAssistentRoh] = useState<AssistentModus | null>(() => {
    const m = lies('merkan.assistent.offen');
    return m === 'fenster' || m === 'gross' ? m : null;
  });
  // Bedienvorlieben gelten auf allen Geräten (benutzer_einstellungen.oberflaeche), lokal nur als Vorbelegung
  const [starterWert, setStarterWert] = useVorliebe<StarterArt>('assistent.starter', 'leiste');
  const starter: StarterArt = starterWert === 'symbol' ? 'symbol' : 'leiste';
  const setAssistent = (m: AssistentModus | null) => { setAssistentRoh(m); schreib('merkan.assistent.offen', m ?? ''); };
  const setStarter = (a: StarterArt) => setStarterWert(a);
  const [neueNachrichten, setNeueNachrichten] = useState<number | null>(null);
  const [postfachNeu, setPostfachNeu] = useState<number | null>(null);
  const [offeneVorschlaege, setOffeneVorschlaege] = useState<number | null>(null);
  const ort = useLocation();
  const inhaltRef = useRef<HTMLElement>(null);
  useErscheinen(inhaltRef, `${ort.pathname}|${mandant?.mandant_id ?? ''}`);

  // Barcode-Scanner: Hardware-Scanner überall (außerhalb von Eingabefeldern), Kamera über den Knopf in der Kopfleiste
  const darfScannen = hat('stamm', 'verkauf', 'einkauf', 'versand');
  const [scan, setScan] = useState<{ code: string | null } | null>(null);
  useEffect(() => {
    if (!darfScannen) return;
    const ab = handscannerLauschen((code) => setScan({ code }));
    const offen = (e: Event) => setScan({ code: (e as CustomEvent<string | null>).detail ?? null });
    window.addEventListener('merkan:scan', offen);
    return () => { ab(); window.removeEventListener('merkan:scan', offen); };
  }, [darfScannen]);

  // Frage an Hugo aus einer Begriffserklärung: Fenster öffnen, die Frage sendet der Assistent selbst
  useEffect(() => {
    const h = () => { if (hat('ki')) setAssistentRoh((a) => { const n = a ?? 'fenster'; schreib('merkan.assistent.offen', n); return n; }); };
    window.addEventListener('merkan:hugo-oeffnen', h);
    return () => window.removeEventListener('merkan:hugo-oeffnen', h);
  }, [hat]);

  // Tastenkürzel: Strg K Assistent, Strg / Suche, Esc schließt (GR-13)
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (hat('ki')) { setSuche(false); setAssistentRoh((a) => { const n = a ? null : 'fenster'; schreib('merkan.assistent.offen', n ?? ''); return n; }); }
      }
      if (mod && (e.key === '/' || e.code === 'Slash')) {
        e.preventDefault();
        setAssistentRoh((a) => (a === 'gross' ? 'fenster' : a));
        setSuche((s) => !s);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [hat]);

  // Beim Seitenwechsel schließt die Suche; das Assistenten-Fenster bleibt, das große Panel wird wieder zum Fenster
  useEffect(() => { setSuche(false); setAssistentRoh((a) => (a === 'gross' ? 'fenster' : a)); }, [ort.pathname]);

  // Zähler im Menü: nur neue Eingänge ohne Vorgang, also genau die Karten in „Neu“ der Standardansicht des Service Hub.
  // Nachrichten, die schon einem Vorgang zugeordnet sind, zeigt die Vorgangskarte (Briefsymbol mit Anzahl).
  useEffect(() => {
    if (!mandant || !hat('hub')) return;
    let aktiv = true;
    const laden = () => supabase.from('nachrichten').select('id', { count: 'exact', head: true })
      .eq('mandant_id', mandant.mandant_id).in('richtung', ['ein', 'telefon']).eq('status', 'neu').is('vorgang_id', null).eq('im_hub', true).is('ablage', null)
      .then(({ count }) => { if (aktiv) setNeueNachrichten(count ?? null); });
    // Postfach: ungelesene, unbearbeitete Mails aus persönlichen Postfächern (RLS zeigt nur eigene und freigegebene)
    const uid = session?.user.id;
    const postfach = () => uid ? supabase.from('nachrichten').select('id', { count: 'exact', head: true })
      .eq('mandant_id', mandant.mandant_id).eq('richtung', 'ein').eq('im_hub', false).is('vorgang_id', null).is('ablage', null)
      .neq('status', 'erledigt').not('gelesen_von', 'cs', `{${uid}}`)
      .then(({ count, error }) => { if (aktiv) setPostfachNeu(error ? null : count ?? null); }) : undefined;
    void laden(); void postfach();
    const takt = window.setInterval(() => { void laden(); void postfach(); }, 60_000);
    return () => { aktiv = false; window.clearInterval(takt); };
  }, [mandant, hat, ort.pathname, session?.user.id]);

  // Zähler am Abgleich: offene Änderungsvorschläge für den Stamm
  useEffect(() => {
    if (!mandant || !hat('stamm.bearbeiten')) return;
    let aktiv = true;
    void supabase.from('stamm_vorschlaege').select('id', { count: 'exact', head: true }).eq('mandant_id', mandant.mandant_id).eq('status', 'offen')
      .then(({ count, error }) => { if (aktiv) setOffeneVorschlaege(error ? null : count ?? null); });
    return () => { aktiv = false; };
  }, [mandant, hat, ort.pathname]);

  const abschnitte = menue().map((a) => ({ ...a, punkte: a.punkte.filter((p) => !p.rechte || hat(...p.rechte)) })).filter((a) => a.punkte.length);
  const email = session?.user.email ?? '';

  // Menü einklappbar: nur Symbole, der Inhalt nutzt die frei werdende Breite
  const [zuWert, setZuWert] = useVorliebe<'0' | '1'>('navi.zu', '0');
  const zu = zuWert === '1';
  const setZu = (v: boolean) => setZuWert(v ? '1' : '0');

  // Mobil: Seitenleiste als Sheet von links (DS-05). Schließt bei Seitenwechsel und mit Esc.
  const [sheet, setSheet] = useState(false);
  useEffect(() => { setSheet(false); }, [ort.pathname]);
  // Geführter Zugriff: Menü-Blatt öffnen oder schließen, wenn ein Schritt im Menü liegt
  useEffect(() => {
    const auf = () => setSheet(true);
    const zu = () => setSheet(false);
    window.addEventListener('merkan:menue-oeffnen', auf);
    window.addEventListener('merkan:menue-schliessen', zu);
    return () => { window.removeEventListener('merkan:menue-oeffnen', auf); window.removeEventListener('merkan:menue-schliessen', zu); };
  }, []);
  useEffect(() => {
    if (!sheet) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheet(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [sheet]);

  return (
    <div className={`rahmen${zu ? ' navi-zu' : ''}${sheet ? ' sheet-offen' : ''}`}>
      {sheet && <div className="schleier navi-schleier" onClick={() => setSheet(false)} />}
      <aside className="navi" id="hauptmenue" aria-label={t('Hauptmenü')}>
        <div className="navi-marke">
          <Logo className="navi-lockup" hoehe={24} /><Logo art="zeichen" className="navi-zeichen" hoehe={24} />
          {mandant?.kurzname && <span className="navi-kuerzel" title={mandant.name}>{mandant.kurzname.toUpperCase()}</span>}
          <button type="button" data-tour="navi-umschalter" className="navi-umschalter" onClick={() => setZu(!zu)} title={zu ? t('Menü ausklappen') : t('Menü einklappen')} aria-label={zu ? t('Menü ausklappen') : t('Menü einklappen')} aria-expanded={!zu}>
            {zu ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>
        <MandantKasten />
        {abschnitte.map((a) => (
          <div key={a.abschnitt ?? 'start'} style={{ display: 'grid', gap: 1 }}>
            {a.abschnitt && <div className="navi-abschnitt">{a.abschnitt}</div>}
            {a.punkte.map((p) => (
              <NavLink key={p.zu} to={p.zu === '/wiki' && !hat('wiki') ? '/wiki/anleitungen' : p.zu} end={p.ende} title={zu ? p.name : undefined} className={p.unter ? 'navi-unter' : undefined} data-tour={`menue-${p.zu.replace(/^\//, '') || 'uebersicht'}`}>
                {p.icon}
                <span className="navi-text">{p.name}</span>
                {p.zu === '/hub' && neueNachrichten ? <span className="zaehler">{neueNachrichten}</span> : null}
                {p.zu === '/postfach' && postfachNeu ? <span className="zaehler">{postfachNeu}</span> : null}
                {p.zu === '/abgleich' && offeneVorschlaege ? <span className="zaehler">{offeneVorschlaege}</span> : null}
              </NavLink>
            ))}
          </div>
        ))}
        <div className="navi-fuss">
          <button type="button" className="navi-knopf" onClick={() => { setSheet(false); supportOeffnen({ quelle: 'hilfe' }); }} title={zu ? t('Support: Anliegen an parlinex') : t('Anliegen an parlinex senden')} data-tour="menue-support"><LifeBuoy /><span className="navi-text">{t('Support')}</span></button>
          <NavLink to="/einstellungen" title={zu ? t('Einstellungen') : undefined} data-tour="menue-einstellungen"><IcEinstellungen /><span className="navi-text">{t('Einstellungen')}</span></NavLink>
          <div className="navi-benutzer" title={email} data-tour="navi-benutzer">
            <span className="avatar">{initialen(email.split('@')[0])}</span>
            <span className="email">{email}</span>
            <Knopf
              art="leise" klein icon={<Languages />}
              onClick={() => spracheSetzen(sprache() === 'de' ? 'en' : 'de')}
              aria-label={sprache() === 'de' ? 'Switch to English' : 'Auf Deutsch umstellen'}
              title={sprache() === 'de' ? 'Switch to English' : 'Auf Deutsch umstellen'}
              style={{ marginLeft: 'auto', gap: 4, fontSize: 11, fontWeight: 600, letterSpacing: '.04em' }}
            ><span className="navi-text">{sprache() === 'de' ? 'EN' : 'DE'}</span></Knopf>
            <Knopf art="leise" klein icon={<LogOut />} onClick={abmelden} aria-label={t('Abmelden')} title={t('Abmelden')} />
          </div>
        </div>
      </aside>

      <div className="haupt">
        <AnsichtAlsLeiste />
        <div className="kopfleiste nicht-drucken">
          <button type="button" data-tour="kopf-menue" className="kopf-menue" onClick={() => setSheet(true)} aria-label={t('Menü öffnen')} aria-controls="hauptmenue" aria-expanded={sheet}>
            <IcMehr />
          </button>
          <label className="kopf-suche" data-tour="kopf-suche">
            <Search />
            <input
              type="search" value="" aria-label={t('Suchen')} placeholder={schmal ? t('Suchen …') : aktuelleBranche().schiffe ? t('Suchen nach Vorgang, Kunde, Schiff, IMO, Teilenummer …') : t('Suchen nach Vorgang, Kunde, Artikel, Teilenummer, Beleg …')}
              onFocus={(e) => { e.currentTarget.blur(); setSucheAnfang(''); setSuche(true); }}
              onChange={(e) => { setSucheAnfang(e.target.value); setSuche(true); }}
            />
          </label>
          <div className="kopf-rechts">
            <Knopf
              art="leise"
              icon={einstellungen.praesentation ? <EyeOff /> : <Eye />}
              onClick={() => einstellungSetzen({ praesentation: !einstellungen.praesentation })}
              title={einstellungen.praesentation ? t('Präsentationsmodus beenden') : t('Präsentationsmodus: Beträge ausblenden')}
              aria-pressed={einstellungen.praesentation}
              data-tour="kopf-praesentation"
            />
            {darfScannen && <Knopf art="leise" data-tour="kopf-scan" icon={<ScanBarcode />} onClick={() => setScan({ code: null })} title={t('Scannen: Barcode per Kamera oder Handscanner')} aria-label={t('Scannen')} />}
            <DarstellungKnopf />
            <Glocke />
            {hat('ki') && (
              <Knopf data-tour="kopf-hugo" icon={<IcKiAssistent />} onClick={() => setAssistent(assistent ?? 'fenster')} title={t('{ki}, dein KI-Assistent')}>
                <span className="nur-breit">{t('{ki}')}</span>
              </Knopf>
            )}
          </div>
        </div>
        <KiHinweis />
        <main className="inhalt" ref={inhaltRef} key={mandant?.mandant_id ?? 'kein'}>{children}</main>
      </div>

      <nav data-tour="mobil-leiste" className="mobil-leiste nicht-drucken" aria-label={t('Schnellzugriff')}>
        <NavLink to="/" end><IcDashboard />{t('Übersicht')}</NavLink>
        {hat('hub') ? <NavLink to="/hub"><IcPosteingang />{t('Hub')}</NavLink> : <NavLink to="/kunden"><IcKunden />{t('Kunden')}</NavLink>}
        {hat('verkauf') ? <NavLink to="/vorgaenge"><IcAuftraege />{t('Vorgänge')}</NavLink> : aktuelleBranche().schiffe ? <NavLink to="/schiffe"><IcSeefracht />{t('Schiffe')}</NavLink> : <NavLink to="/artikel"><IcArtikel />{t('Artikel')}</NavLink>}
        {hat('rw') ? <NavLink to="/rechnungswesen"><IcRechnungen />{t('Belege')}</NavLink> : <NavLink to={hat('wiki') ? '/wiki' : '/wiki/anleitungen'}><IcWiki />{t('Wiki')}</NavLink>}
        <button type="button" onClick={() => setSheet(true)} aria-controls="hauptmenue" aria-expanded={sheet}><IcMehr />{t('Mehr')}</button>
      </nav>

      <Suspense fallback={null}>
        {suche && <Suche anfang={sucheAnfang} schliessen={() => { setSuche(false); setSucheAnfang(''); }} />}
        {assistent && hat('ki') && (
          <Assistent
            modus={assistent}
            setModus={(m) => setAssistent(m)}
            minimieren={() => { setAssistent(null); setStarter('leiste'); }}
            beenden={() => { setAssistent(null); setStarter('symbol'); }}
          />
        )}
      </Suspense>
      {!assistent && hat('ki') && (
        <Starter art={starter} oeffnen={() => setAssistent('fenster')} beenden={() => setStarter('symbol')} />
      )}
      <HilfeSymbol ueberHugo={!assistent && hat('ki')} nebenHugo={assistent === 'fenster' && hat('ki')} verdeckt={assistent === 'gross'} />
      <Tour />
      <DokumentLesen />
      <DokumentAnsicht />
      {scan && <Suspense fallback={null}><ScanFenster key={scan.code ?? 'leer'} start={scan.code} schliessen={() => setScan(null)} /></Suspense>}
    </div>
  );
}

/** Menüseite für Mobilgeräte */
export function MenueSeite() {
  const { hat, abmelden } = useSession();
  const abschnitte = menue().map((a) => ({ ...a, punkte: a.punkte.filter((p) => !p.rechte || hat(...p.rechte)) })).filter((a) => a.punkte.length);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <h1>{t('Menü')}</h1>
      {abschnitte.map((a) => (
        <div key={a.abschnitt ?? 'start'} className="block">
          {a.abschnitt && <div className="block-kopf"><h3>{a.abschnitt}</h3></div>}
          <ul className="block-liste">
            {a.punkte.map((p) => <li key={p.zu}><NavLink to={p.zu === '/wiki' && !hat('wiki') ? '/wiki/anleitungen' : p.zu} style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1 }}>{p.icon}{p.name}</NavLink></li>)}
          </ul>
        </div>
      ))}
      <div className="block"><ul className="block-liste">
        <li><button type="button" className="kn kn-leise" onClick={() => supportOeffnen({ quelle: 'hilfe' })}><LifeBuoy />{t('Support')}</button></li>
        <li><NavLink to="/einstellungen" style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1 }}><IcEinstellungen />{t('Einstellungen')}</NavLink></li>
        <li><button type="button" className="kn kn-leise" onClick={abmelden}><LogOut />{t('Abmelden')}</button></li>
      </ul></div>
    </div>
  );
}
